import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";

import {
  Paper,
  Typography,
  Box,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import { useClientInfo } from "../utils/commonImports";
import { drawClientHeader } from "../utils/reportClientHeader";
import { addPdfFooter } from "../utils/reportFooter";


export default function TariffReport() {
  const { clientId, client } = useClientInfo();

  /* =========================
     STATE
  ========================= */
  const [rows, setRows] = useState([]);
  const [tariffType, setTariffType] = useState("");

  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [customerId, setCustomerId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [vendorId, setVendorId] = useState("");

  /* =========================
     LOAD DROPDOWNS
  ========================= */
  useEffect(() => {
    if (!clientId) return;

    axios
      .get("/api/customers", { params: { client_id: clientId } })
      .then((res) => setCustomers(res.data || []));

    axios
      .get("/api/vehicles", { params: { client_id: clientId } })
      .then((res) => setVehicles(res.data || []));

    axios
      .get("/api/vendors", { params: { client_id: clientId } })
      .then((res) => setVendors(res.data || []));
  }, [clientId]);

  /* =========================
     SALE OR PURCHASE MODE
  ========================= */
  const isSale = ["customer", "customer_vehicle", "general_customer"].includes(
    tariffType,
  );

  /* =========================
     LOAD REPORT
  ========================= */
  const loadReport = async () => {
    if (
      (tariffType === "customer" && !customerId) ||
      (tariffType === "customer_vehicle" && !customerId) ||
      (tariffType === "vehicle" && !vehicleId) ||
      (tariffType === "vendor" && !vendorId)
    ) {
      alert("Please select required entity");
      return;
    }

    const res = await axios.get("/api/tariffs/report", {
      params: {
        client_id: clientId,
        tariff_type: tariffType,
        customer_id: customerId || undefined,
        vehicle_id: vehicleId || undefined,
        vendor_id: vendorId || undefined,
      },
    });

    setRows(res.data || []);
  };

  /* =========================
     COLUMN DEFINITION (SINGLE SOURCE OF TRUTH)
  ========================= */
  const columns = [
    // { key: "entity_type", label: "Entity Type" },
    // { key: "entity_name", label: "Entity Name" },
    { key: "vehicle_group", label: "Vehicle Group" },
    { key: "location", label: "Location" },

    { key: "slab_kms", label: "KM Slab" },
    { key: "slab_hours", label: "Hour Slab" },
    { key: "slab_rate", label: "Slab Rate" },

    { key: "extra_km_rate", label: "Extra KM" },
    { key: "extra_hr_rate", label: "Extra Hr" },
    { key: "batta_rate", label: "Batta" },
    { key: "airport_rate", label: "Airport" },
    { key: "employee_rate", label: "Employee" },
    { key: "active", label: "Active" },
  ];

  // =========================
  // ENTITY DISPLAY NAME
  // =========================
  const getEntityDisplayName = () => {
    if (tariffType === "customer" || tariffType === "customer_vehicle") {
      const c = customers.find((c) => c.customerid === Number(customerId));
      return c ? c.customername : "General (All Customers)";
    }

    if (tariffType === "vehicle") {
      const v = vehicles.find((v) => v.vehicle_id === Number(vehicleId));
      return v ? v.vehicle_number : "General (All Vehicles)";
    }

    if (tariffType === "vendor") {
      const v = vendors.find((v) => v.vendorid === Number(vendorId));
      return v ? v.vendorname : "General (All Vendors)";
    }

    if (tariffType === "general_customer") {
      return "General (All Customers)";
    }

    if (tariffType === "general_vehicle") {
      return "General (All Vehicles / Vendors)";
    }

    return "";
  };

  /* =========================
     NORMALIZE ROW
  ========================= */
  const normalizeRow = (r) => ({
    entity_type: r.entity_type,
    // entity_name: getEntityName(r),
    vehicle_group: r.vehicle_group || "-",
    vehicle_type: r.vehicle_type || "-",
    location: r.location || "-",

    airport_rate: r.airport_rate || 0,
    slab_kms: r.slab_kms || "-",
    slab_hours: r.slab_hours || "-",
    slab_rate: isSale ? r.sale_slab_rate : r.purchase_slab_rate,

    extra_km_rate: r.extra_km_rate || 0,
    extra_hr_rate: r.extra_hr_rate || 0,
    batta_rate: r.batta_rate || 0,
    employee_rate: r.employee_rate || 0,
    active: r.active ? "Yes" : "No",
  });

  const normalizedRows = rows.map(normalizeRow);

  /* =========================
     PDF
  ========================= */
 const generatePDF = () => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const headerEndY = drawClientHeader(doc, client);

  // Title
  doc.setFontSize(13);
  doc.setFont(undefined, "bold");
  doc.text("Tariff Report", 14, headerEndY);

  // Entity (ONCE)
//   doc.setFontSize(10);
//   doc.setFont(undefined, "normal");
//   doc.text("Entity", 14, headerEndY + 6);

  doc.setFontSize(12);
  doc.setFont(undefined, "bold");
  doc.text(getEntityDisplayName(), 14, headerEndY + 12);

  const tableStartY = headerEndY + 18;

  const columnsPDF = [
    "Vehicle Group",
    "Location",
    "Hour Slab",
    "KM Slab",
    "Slab Rate",
    "Extra KM",
    "Extra Hr",
    "Batta",
    "Airport",
    "Employee",
    "Active",
  ];

  const body = normalizedRows.map((r) => [
    r.vehicle_group,
    r.location,
    r.slab_hours,
    r.slab_kms,
    Number(r.slab_rate).toFixed(2),
    Number(r.extra_km_rate).toFixed(2),
    Number(r.extra_hr_rate).toFixed(2),
    Number(r.batta_rate).toFixed(2),
      Number(r.airport_rate).toFixed(2),
    Number(r.employee_rate).toFixed(2),
    r.active,
  ]);

  autoTable(doc, {
    head: [columnsPDF],
    body,
    startY: tableStartY,
    theme: "grid",

    styles: {
      fontSize: 9,
      cellPadding: 3,
      lineWidth: 0.2,
      lineColor: [0, 0, 0],
      textColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [240, 240, 240],
      fontStyle: "bold",
      lineWidth: 0.3,
    },
    tableLineWidth: 0.3,
  });

  // =========================
  // FOOTER (ALWAYS LAST ❗)
  // =========================
  addPdfFooter(doc);

  return doc;
};

  const handlePrint = () => {
    const doc = generatePDF();
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.onload = () => iframe.contentWindow.print();
  };

  const handlePDF = () => generatePDF().save("Tariff_Report.pdf");

  const handleExcel = () => {
    const data = normalizedRows.map((r) => {
      const obj = {};
      columns.forEach((c) => (obj[c.label] = r[c.key]));
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tariffs");
    XLSX.writeFile(wb, "Tariff_Report.xlsx");
  };

  /* =========================
     UI
  ========================= */
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight={700} mb={1.5}>
        Tariff Report
      </Typography>

      {/* FILTERS */}
      <Box display="flex" gap={1.5} mb={1.5} flexWrap="wrap">
        <TextField
          size="small"
          select
          label="Tariff Type"
          value={tariffType}
          onChange={(e) => {
            setTariffType(e.target.value);
            setCustomerId("");
            setVehicleId("");
            setVendorId("");
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">Select</MenuItem>
          <MenuItem value="customer">Customer</MenuItem>
          <MenuItem value="customer_vehicle">Customer – Vehicle</MenuItem>
          <MenuItem value="vehicle">Vehicle</MenuItem>
          <MenuItem value="vendor">Vendor</MenuItem>
          <MenuItem value="general_customer">General – Customer</MenuItem>
          <MenuItem value="general_vehicle">General – Vehicle/Vendor</MenuItem>
        </TextField>

        {["customer", "customer_vehicle"].includes(tariffType) && (
          <TextField
            size="small"
            select
            label="Customer"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="">Select Customer</MenuItem>
            {customers.map((c) => (
              <MenuItem key={c.customerid} value={c.customerid}>
                {c.customername}
              </MenuItem>
            ))}
          </TextField>
        )}

        {tariffType === "vehicle" && (
          <TextField
            size="small"
            select
            label="Vehicle"
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Select Vehicle</MenuItem>
            {vehicles.map((v) => (
              <MenuItem key={v.vehicle_id} value={v.vehicle_id}>
                {v.vehicle_number}
              </MenuItem>
            ))}
          </TextField>
        )}

        {tariffType === "vendor" && (
          <TextField
            size="small"
            select
            label="Vendor"
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Select Vendor</MenuItem>
            {vendors.map((v) => (
              <MenuItem key={v.vendorid} value={v.vendorid}>
                {v.vendorname}
              </MenuItem>
            ))}
          </TextField>
        )}

        <Button variant="contained" size="small" onClick={loadReport}>
          Search
        </Button>
        <Button variant="outlined" size="small" onClick={handlePrint}>
          Print
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="error"
          onClick={handlePDF}
        >
          PDF
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="success"
          onClick={handleExcel}
        >
          Excel
        </Button>
      </Box>

      <Typography variant="body2" sx={{ mb: 1, color: "#555" }}>
        <strong>Entity :</strong> {getEntityDisplayName()}
      </Typography>

      {/* TABLE */}
      <Table size="small">
        <TableHead sx={{ background: "#f1f5f9" }}>
          <TableRow>
            {columns.map((c) => (
              <TableCell key={c.key} sx={{ fontSize: 13, fontWeight: 700 }}>
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {normalizedRows.map((r, i) => (
            <TableRow key={i}>
              {columns.map((c) => (
                <TableCell key={c.key} sx={{ fontSize: 13 }}>
                  {r[c.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}

          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} align="center">
                No records found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
}
